import React, { useContext, useEffect, useState } from 'react';
import { path, reduce } from 'ramda';
import { Crisp } from 'crisp-sdk-web';
import { navigate } from 'gatsby';
import { User, CreditCard, FileText, LogOut } from 'lucide-react';
import moment from 'moment';

import SettingsSkeleton from '../../components/settingsSkeleton';
import AppContext from '../../context/app';
import { getFirebase } from '../../utils/signin';

const trialDaysPeriod = 14;

const stripeEndpointKey = process.env.GATSBY_NODE_ENV === 'development' ? 'j8jv' : 'frj0';

const getTrialLimits = (createdAt) => {
    const trialExpireAt = moment(createdAt).add(trialDaysPeriod, 'd');
    const remainingTrialDays = trialExpireAt.diff(moment(), 'd');
    return {
        isTrial: true,
        remainingTrialDays: isNaN(remainingTrialDays) || remainingTrialDays < 0 ? 0 : remainingTrialDays,
        onlineOrderEnabled: true,
        loyaltyCardEnabled: true,
        numberOfMenus: 1,
        numberOfMenuItems: 15,
        preventAllergiesEnabled: true,
        images: true
    };
};

const extrasCapabilities = {
    onlineOrder: {
        onlineOrderEnabled: true
    },
    loyaltyCard: {
        loyaltyCardEnabled: true
    }
};

export const getCapabilitiesByExtras = (extras = []) => {
    return reduce(
        (acc, curr) => {
            const extraCapabilities = extrasCapabilities[curr];
            if (!extraCapabilities) return acc;

            return {
                ...acc,
                ...extraCapabilities
            };
        },
        {},
        extras
    );
};

const getLimitsByOffer = (offer, extrasRaw = '{}') => {
    const extras = JSON.parse(extrasRaw);
    switch (offer) {
        case 'dish':
            return {
                ...extras,
                offer,
                numberOfMenus: 5000,
                numberOfMenuItems: 5000,
                preventAllergiesEnabled: true,
                images: false
            };
        case 'dessert':
            return {
                ...extras,
                offer,
                numberOfMenus: 5000,
                numberOfMenuItems: 5000,
                preventAllergiesEnabled: true,
                loyaltyCardEnabled: true,
                images: true
            };
        default:
            return {
                ...extras,
                offer,
                numberOfMenus: 1,
                numberOfMenuItems: 15,
                preventAllergiesEnabled: false,
                images: false
            };
    }
};

export const getUserLimits = async (userId, createdAt) => {
    const db = getFirebase().firestore();
    try {
        const snapshot = await db
            .collection('customers')
            .doc(userId)
            .collection('subscriptions')
            .where('status', 'in', ['trialing', 'active'])
            .where('cancel_at', '==', null)
            .get();
        const isCreatedUnder7days = moment(createdAt).isAfter(moment().subtract(trialDaysPeriod, 'd'));
        if (snapshot.empty) {
            // if (!isCreatedUnder7days) return null;

            return getTrialLimits(createdAt);
        }

        const subscription = snapshot.docs[0].data();
        const role = subscription.role;
        const extras = subscription.metadata?.extras;
        return getLimitsByOffer(role, extras);
    } catch (e) {
        return null;
    }
};

function Account() {
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMutation, setIsLoadingMutation] = useState(false);
    const [hasNoActivePlan, setHasNoActivePlan] = useState(false);
    const [userOffer, setUserOffer] = useState(null);
    const [userOfferPrice, setUserOfferPrice] = useState(null);
    const [showError, setShowError] = useState(false);
    const { customer, user } = useContext(AppContext);

    useEffect(() => {
        const unregisterAuthObserver = getFirebase()
            .auth()
            .onAuthStateChanged((user) => {
                const db = getFirebase().firestore();
                db.collection('customers')
                    .doc(user.uid)
                    .collection('subscriptions')
                    .where('status', 'in', ['trialing', 'active'])
                    .onSnapshot(async (snapshot) => {
                        if (snapshot.empty) {
                            setHasNoActivePlan(true);
                            setIsLoading(false);
                            return;
                        }
                        const subscription = snapshot.docs[0].data();
                        const priceData = (await subscription.price.get()).data();
                        setUserOffer(path(['items', 0, 'price', 'product', 'name'], subscription));
                        setUserOfferPrice(
                            priceData
                                ? `${new Intl.NumberFormat('fr-FR', {
                                      style: 'currency',
                                      currency: priceData?.currency ? priceData?.currency : 'eur'
                                  }).format((priceData.unit_amount / 100).toFixed(2))} per ${
                                      priceData.interval
                                  }`
                                : 'N/A'
                        );
                        setIsLoading(false);
                    });
            });
        return () => unregisterAuthObserver(); // Make sure we un-register Firebase observers when the component unmounts.
    }, []);
    const handleShowError = () => {
        setShowError(true);
        setIsLoadingMutation(false);
        setTimeout(() => {
            setShowError(false);
        }, 3000);
    };

    const openChat = () => {
        Crisp.chat.open();
        Crisp.chat.onChatOpened(() => {
            Crisp.user.setNickname(user.displayName);
            Crisp.user.setEmail(user.email);
        });
    };
    const handleLogOut = () => {
        getFirebase().auth().signOut();
        navigate('/');
    };
    const handleOpenClientPortal = async () => {
        const hasCustomJob = !!customer?.hasCustomization;
        try {
            setIsLoadingMutation(true);
            const functionRef = getFirebase()
                .app()
                .functions()
                .httpsCallable(`ext-firestore-stripe-payments-${stripeEndpointKey}-createPortalLink`);
            const { data } = await functionRef({
                returnUrl: window.location.href,
                configuration: hasCustomJob
                    ? process.env.GATSBY_BILLING_PORTAL
                    : process.env.GATSBY_ALTERNATE_BILLING_PORTAL
            });
            window.location.assign(data.url);
            setIsLoadingMutation(false);
        } catch (e) {
            handleShowError();
        }
    };
    return (
        <div>
            {isLoadingMutation && (
                <div className="w-full h-full fixed block top-0 left-0 bg-white opacity-75 z-50 flex items-center justify-center">
                    <svg
                        className="animate-spin ml-1 mr-3 h-28 w-28 text-primary-400"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                    >
                        <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            stroke-width="4"
                        ></circle>
                        <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                    </svg>
                </div>
            )}
            <div className="pt-4 pb-24">
                <div className="container mx-auto">
                    {isLoading ? (
                        <SettingsSkeleton />
                    ) : (
                        <div className="p-8">
                            {/* Welcome Section */}
                            <div className="mb-8">
                                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                                    Welcome back, {getFirebase().auth()?.currentUser?.displayName}
                                </h3>
                                <p className="text-gray-600">
                                    Manage your account settings and subscription details below.
                                </p>
                            </div>

                            {/* Account Management */}
                            <div className="bg-white border border-gray-200 rounded-xl p-6">
                                <h4 className="text-lg font-semibold text-gray-800 mb-6">
                                    Account Management
                                </h4>

                                <div className="space-y-4">
                                    {/* Account & Subscription */}
                                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                        <div className="flex items-center space-x-3">
                                            <div className="bg-blue-100 p-2 rounded-lg">
                                                <User className="w-5 h-5 text-blue-600" />
                                            </div>
                                            <div>
                                                <h5 className="font-medium text-gray-800">
                                                    Account & Subscription
                                                </h5>
                                                {!hasNoActivePlan && (
                                                    <p className="text-sm text-gray-600">
                                                        {userOffer} Plan - {userOfferPrice}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        {hasNoActivePlan ? (
                                            <div className="flex items-center space-x-2">
                                                <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                                                <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-medium">
                                                    Inactive
                                                </span>
                                            </div>
                                        ) : (
                                            <div className="flex items-center space-x-2">
                                                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                                                    Active
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Client Portal Access */}
                                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                        <div className="flex items-center space-x-3">
                                            <div className="bg-orange-100 p-2 rounded-lg">
                                                <CreditCard className="w-5 h-5 text-orange-600" />
                                            </div>
                                            <div>
                                                <h5 className="font-medium text-gray-800">
                                                    Billing & Subscription
                                                </h5>
                                                <p className="text-sm text-gray-600">
                                                    View invoices, modify subscription, or update payment
                                                    method
                                                </p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={handleOpenClientPortal}
                                            className="bg-yellow-500 hover:bg-orange-600 text-white px-6 py-2 rounded-lg font-medium transition-colors duration-200"
                                        >
                                            Access Portal
                                        </button>
                                    </div>

                                    {/* Support */}
                                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                        <div className="flex items-center space-x-3">
                                            <div className="bg-blue-100 p-2 rounded-lg">
                                                <FileText className="w-5 h-5 text-blue-600" />
                                            </div>
                                            <div>
                                                <h5 className="font-medium text-gray-800">Help & Support</h5>
                                                <p className="text-sm text-gray-600">
                                                    Get help with your account or technical issues
                                                </p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={openChat}
                                            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors duration-200"
                                        >
                                            Contact Support
                                        </button>
                                    </div>

                                    {/* Logout */}
                                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                        <div className="flex items-center space-x-3">
                                            <div className="bg-red-100 p-2 rounded-lg">
                                                <LogOut className="w-5 h-5 text-red-600" />
                                            </div>
                                            <div>
                                                <h5 className="font-medium text-gray-800">Sign Out</h5>
                                                <p className="text-sm text-gray-600">
                                                    Securely log out of your ShopSync account
                                                </p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={handleLogOut}
                                            className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg font-medium transition-colors duration-200"
                                        >
                                            Log Out
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {showError && (
                <div
                    className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded fixed bottom-6 w-2/6 left-0 right-0 ml-auto mr-auto"
                    role="alert"
                >
                    <strong className="font-bold">Error! </strong>
                    <span className="block sm:inline">An error occurred, please try again later.</span>
                    <span className="absolute top-0 bottom-0 right-0 px-4 py-3">
                        <svg
                            className="fill-current h-6 w-6 text-red-500"
                            role="button"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                        >
                            <title>Close</title>
                            <path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z" />
                        </svg>
                    </span>
                </div>
            )}
        </div>
    );
}

export default Account;
